import * as Yup from 'yup';
import { useTranslation } from '@klv/hooks/useTranslation';
import TextInput from '@klv/molecules/form-field/text';
import TextareaInput from '@klv/molecules/form-field/textarea';
import { MFA_SUPPORT_ZENDESK_ID } from '@klv/organisms/form-account-access/fields/mfa_support_zendesk_fields';
import { useMfaSupportFields } from '@klv/organisms/form-account-access/fields/useMfaSupportFields';

import type { ISupportFormFieldContainer, IFieldFor } from '@klv/organisms/form-account-access/types';

const {
  COMPANY_TEST_URL,
  ORG_MAILING_ADDRESS,
  PROFILE_NAMES,
  LAST_LOGIN_DATE,
  CAMPAIGN_FLOW_NAME,
  OTHER_EMAILS,
  RECENT_INVOICE,
} = MFA_SUPPORT_ZENDESK_ID;

const MfaSupportFieldFor = ({ field, backgroundColor }: IFieldFor) => {
  const { label, fieldType, formName, placeholder } = field;
  if (fieldType === 'multilineText') {
    return (
      <TextareaInput
        label={label}
        name={formName}
        placeholder={placeholder}
        backgroundColor={backgroundColor}
      />
    );
  }
  if (fieldType === 'text') {
    return (
      <TextInput
        label={label}
        name={formName}
        placeholder={placeholder}
        backgroundColor={backgroundColor}
      />
    );
  }
  return null;
};

export const MfaSupportFields = ({
  backgroundColor,
}: ISupportFormFieldContainer) => {
  const MfaFields = useMfaSupportFields();
  // this is the order in which they will display
  return [
    MfaFields[PROFILE_NAMES],
    MfaFields[LAST_LOGIN_DATE],
    MfaFields[CAMPAIGN_FLOW_NAME],
    MfaFields[OTHER_EMAILS],
    MfaFields[COMPANY_TEST_URL],
    MfaFields[ORG_MAILING_ADDRESS],
    MfaFields[RECENT_INVOICE],
  ].map((field) => (
    <MfaSupportFieldFor key={field.formName} field={field} backgroundColor={backgroundColor} />
  ));
};

export const useValidationSchemaYupObjectT = () => {
  const { t } = useTranslation();

  const defaultValidation = {
    'name': Yup.string().required(
      t('organisms_forms_accountAccessForm_error_required'),
    ),
    'accountName': Yup.string().required(
      t('organisms_forms_accountAccessForm_error_required'),
    ),
    'email': Yup.string()
      .email(t('organisms_forms_accountAccessForm_error_email'))
      .required(t('organisms_forms_accountAccessForm_error_required')),
    'recipients': Yup.array().of(
      Yup.string().email(
        t('organisms_forms_accountAccessForm_error_issueRecipeients'),
      ),
    ),
    'websiteUrl': Yup.string()
      .url(t('organisms_forms_accountAccessForm_error_websiteUrl'))
      .required(t('organisms_forms_accountAccessForm_error_required')),
    'issueDescription':  Yup.string().when('blockerType', ([issueType], schema) => {
      if (issueType !== 'multifactorAuthentication') {
        return schema.required(
          t('organisms_forms_accountAccessForm_error_required'),
        );
      }
      return schema.notRequired();
    }),
    'g-recaptcha-response': Yup.string(),
  };

  const mfaValidation = Object.fromEntries([
    'profileNames',
    'lastLoginDate',
    'campaignFlowName',
    'otherEmails',
    'companyTestUrl',
    'orgMailingAddress',
    'recentInvoice',
  ].map((key) => [key, Yup.string().when('blockerType', ([issueType], schema) => {
    if (issueType === 'multifactorAuthentication') {
      return schema
        .test(
          key,
          t('organisms_forms_accountAccessForm_error_na_required'),
          (value) => Boolean(value === 'NA' || (value && value.length >= 2)),
        )
        .required(t('organisms_forms_accountAccessForm_error_na_required'));
    }
    return schema.notRequired();
  }),
  ],
  ));

  return Yup.object({
    ...defaultValidation,
    ...mfaValidation,
  });
};
